import React, { useEffect, useRef, useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../lib/backendUrl";

const API_BASE = `${BACKEND_URL}/api`;

function fmtTimeAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/**
 * Inline-editable value with a pencil affordance.
 *
 * Props:
 * - displayValue   string  what we currently render (e.g. "₹6.0Cr" or "—")
 * - paramKey       string  which key in customers.company_parameters to update
 * - rawValue       number  current raw numeric value (so the input shows the
 *                          editable underlying number, not the formatted one)
 * - customerId     string  required to call /customer/params/update
 * - transformIn    fn      raw stored value → editor value (e.g. ₹ → ₹ Cr)
 * - transformOut   fn      editor value → raw stored value
 * - testId         string  for data-testid
 * - lastUpdatedIso string  ISO timestamp for "Last updated Xm ago"
 * - onSaved        fn      called with the server response on successful save
 * - disabled       bool    suppress the edit affordance (e.g. for visitors)
 */
export default function EditableValue({
  displayValue, paramKey, rawValue, customerId,
  transformIn = (v) => (v ?? ""),
  transformOut = (v) => (v === "" ? null : Number(v)),
  testId, lastUpdatedIso, onSaved, disabled = false,
  inputType = "number", inputPlaceholder = "", paramLabel,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(transformIn(rawValue));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(transformIn(rawValue)); }, [rawValue, transformIn]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const save = async () => {
    if (!customerId) { setError("Sign in to edit"); return; }
    setBusy(true); setError(null);
    try {
      const payload = { customer_id: customerId, key: paramKey, value: transformOut(draft) };
      const res = await fetch(`${API_BASE}/customer/params/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { setError("Save failed"); setBusy(false); return; }
      const body = await res.json();
      // Broadcast the update so the global toaster + ripple effect fire.
      try {
        window.dispatchEvent(new CustomEvent("core:param-updated", {
          detail: { key: paramKey, label: paramLabel || paramKey, value: payload.value },
        }));
      } catch (_e) { /* no-op in non-browser env */ }
      if (onSaved) onSaved(body);
      setEditing(false);
    } catch (_e) { setError("Network error"); }
    setBusy(false);
  };

  const cancel = () => { setDraft(transformIn(rawValue)); setEditing(false); setError(null); };
  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  if (disabled) {
    return <span data-testid={testId}>{displayValue}</span>;
  }

  if (editing) {
    return (
      <span data-testid={`${testId}-editing`} className="inline-flex items-center gap-1.5">
        <input
          ref={inputRef}
          type={inputType}
          value={draft}
          placeholder={inputPlaceholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
          data-testid={`${testId}-input`}
          className="h-7 w-[110px] rounded border border-blue-300 bg-white px-2 text-[13px] tabular-nums focus:border-blue-500 focus:outline-none"
        />
        <button
          type="button" disabled={busy} onClick={save}
          data-testid={`${testId}-save`}
          className="inline-flex h-7 w-7 items-center justify-center rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
          aria-label="Save"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        </button>
        <button
          type="button" disabled={busy} onClick={cancel}
          data-testid={`${testId}-cancel`}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
          aria-label="Cancel"
        >
          <X size={12} />
        </button>
        {error && <span data-testid={`${testId}-error`} className="text-[11px] text-red-600">{error}</span>}
      </span>
    );
  }

  const ago = fmtTimeAgo(lastUpdatedIso);
  return (
    <span data-testid={testId} className="group/edit inline-flex items-center gap-1.5">
      <span>{displayValue}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`${testId}-edit-btn`}
        title={ago ? `Last updated ${ago}` : "Edit"}
        className="rounded p-0.5 text-slate-400 opacity-0 transition-opacity hover:text-blue-600 group-hover/edit:opacity-100 focus:opacity-100"
        aria-label="Edit value"
      >
        <Pencil size={11} />
      </button>
      {ago && (
        <span data-testid={`${testId}-updated-ago`} className="hidden text-[10px] text-slate-400 sm:inline">
          · updated {ago}
        </span>
      )}
    </span>
  );
}
